import React, { useState, useEffect, useRef } from 'react';
import 'bulma/css/bulma.min.css';

import { CellActivationStatus, CellValidationStatus, CellHelpStatus, CellHelpMessage, CellValidationResult, CellSubcomponentParameters } from './cellDto.ts';


interface CellModalParameters extends CellSubcomponentParameters {
  modalTitle?: string;
  customModalContent?: (value: any, onValueChange: (input: any) => void) => React.JSX.Element;
}


// Internal state tracking
enum InputValidationState { NotTriggered, Succeeded, Failed };


function CellModal(settings: CellModalParameters) {

  // Value in the DOM input element
  const [inputValue, setInputValue] = useState<any>(settings.value);
  const [validationMessages, setValidationMessages] = useState<React.JSX.Element[]>([]);
  const [validationState, setValidationState] = useState<InputValidationState>(InputValidationState.NotTriggered);
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);


  // This handles keyboard-based navigation or actions in the input field.
  // Changes to the input value are handled in inputOnChange.
  function inputOnKeyDown(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {

    // Escape does not cause a movement to another cell, but we do need to
    // handle the reset operation.
    // We won't halt the event because Escape may need to have other side effects elsewhere.
    if (event.key == "Escape") {
      reset();
      // See the "inputOnBlur" function for some explanation of this hack.
      settings.cellFunctions.close();      
    }
  }


  // We want to give the input element focus when we enter editing mode,
  // but we can only do so after it's been revealed on the page by removing
  // "display:none" from the parent div.  Trying to .focus() on an element that
  // isn't being displayed does nothing.  So we watch justActivated for a delayed effect. 
  useEffect(() => {
    if (settings.activationStatus != CellActivationStatus.Inactive) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [settings.activationStatus]);


  // Deal with changes to the input value.
  // We trigger a short delay before validating,
  // and in the meantime we hide the feedback panel.
  function inputOnChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const value = event.target.value;
    if (debounceTimer) { clearTimeout(debounceTimer); }
    setDebounceTimer(setTimeout(() => inputCompleted(value), 130));
    if (!settings.isReadOnly) {
      setInputValue(value);
    }
    setValidationState(InputValidationState.NotTriggered);
    setValidationMessages([]);
  }


  // Deal with changes to the input value.
  // We trigger a short delay before validating,
  // and in the meantime we hide the feedback panel.
  function onValueChange(value: any) {
    if (debounceTimer) { clearTimeout(debounceTimer); }
    setDebounceTimer(setTimeout(() => inputCompleted(value), 130));
    if (!settings.isReadOnly) {
      setInputValue(value);
    }
    setValidationState(InputValidationState.NotTriggered);
    setValidationMessages([]);
  }


  function inputCompleted(value: any) {
    if (typeof value === "string") {
      value = value.replace(/\u202F|\u00A0/g, ' ');
      value = value.trim();
    }
    const result = settings.cellFunctions.validate(value);
    if (result.status == CellValidationStatus.Success) {
      setValidationState(InputValidationState.Succeeded);
      setValidationMessages([]);
    } else if (result.status == CellValidationStatus.Failure) {
      setValidationState(InputValidationState.Failed);
      setValidationMessages([<p key="validation-result" className="help is-danger">{result.message ?? "Invalid value"}</p>]);
    }
  }


  function save() {
    // If the save is successful we expect settings.value to change
    // which will update the control.
    if (settings.isReadOnly) {
      reset();
      settings.cellFunctions.close();
    } else {
      setValidationState(InputValidationState.NotTriggered);
      setValidationMessages([]);
      const result = settings.cellFunctions.save(inputValue);
      if (result.status != CellValidationStatus.Success) {
        setValidationState(InputValidationState.Failed);
        setValidationMessages([<p key="validation-result" className="help is-danger">{result.message ?? "Error saving value"}</p>]);
      } else {
        settings.cellFunctions.close();
      }
    }
  }


  function reset() {
    setInputValue(settings.value);
    setValidationMessages([]);
    setValidationState(InputValidationState.NotTriggered);
  }


  function resetAndClose() {
    reset();
    settings.cellFunctions.close();
  }


  function inputOnFocus() {
    inputCompleted(settings.value ?? "");
  }

  
  return settings.activationStatus == CellActivationStatus.Inactive ?
    (
      <span></span>
    ) : (

      <div id="modal-add-sample" className={ "modal is-active" } data-ignore-click={ "true" }>
        <div className="modal-background"></div>

        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">{ settings.modalTitle || "Edit Value" }</p>
            <button className="delete" aria-label="close" onClick={ resetAndClose }></button>

          </header>
          <section className="modal-card-body">

            { settings.description && (
              <div className="content">
                <p>
                  { settings.description }
                </p>
              </div>
            )}

            { settings.customModalContent ? (
              settings.customModalContent(inputValue, onValueChange)
            ) : (
              <div className="field">
                <textarea
                    className="textarea"  
                    placeholder={ "Enter value" }
                    onChange={ inputOnChange }
                    autoComplete="off"
                    autoCorrect="off"
                    value={ inputValue || "" }
                    ref={ inputRef }
                    onKeyDown={ inputOnKeyDown }
                    onFocus={ inputOnFocus }
                  />
              </div>
            )}

            <div className="buttons">
              <input
                type="button"
                className="button is-success"
                onClick={ save }
                disabled={ validationState != InputValidationState.Succeeded || settings.isReadOnly }
                value="Save"
              />
              <button className="button" onClick={ resetAndClose }>Cancel</button>
            </div>

            { validationMessages.length > 0 && (
              <article className="message is-danger">
                <div className="message-body">
                  { validationMessages }
                </div>
              </article>
            )}

          </section>

        </div>
      </div>



    );
}


export { CellModal }
